import { config } from '../config.ts';
import { createStorageTexture2D, createUniformBuffer } from '../gpu/buffers.ts';
import { formats } from '../gpu/formats.ts';
import { createComputePipeline } from '../gpu/pipelines.ts';
import type { HeightmapModule } from './heightmap.ts';
import type { BiomeModule } from './biome.ts';
import hashWgsl from './hash.wgsl?raw';
import roadsWgsl from './roads.wgsl?raw';

const wgsl = hashWgsl + '\n' + roadsWgsl;

export interface RoadsModule {
  readonly mask: GPUTexture;
  readonly maskView: GPUTextureView;
  /** Final JFA state (rgba32uint: id, seedX, seedY, _). Kept alive so the buildings
   *  pass can sample distance-to-nearest-seed for downtown clustering. */
  readonly jfa: GPUTexture;
  readonly jfaView: GPUTextureView;
  destroy(): void;
}

/**
 * Phase F — road network as a 1024² r32float mask.
 *
 * 1. Pick `citySeedCount` city centers via stratified hash sampling
 *    (4×2 strata, max-score cell per stratum, urban-flats only).
 * 2. Jump-flood the seed-id texture with halving step (512..1) so every
 *    cell knows its nearest seed-id and seed-position.
 * 3. Voronoi-edge detection → major roads (intensity 1.0).
 * 4. Perturbed-grid overlay anchored at each seed → minor roads (0.5).
 * Roads are gated by distance-to-seed and biome.
 */
export async function createRoads(
  device: GPUDevice,
  height: HeightmapModule,
  biome: BiomeModule,
  opts: { seed: number },
): Promise<RoadsModule> {
  const size = config.heightmapSize;
  const dim: readonly [number, number] = [size, size];
  const dispatch = Math.ceil(size / 8);

  // --- Allocate textures. ---
  // Single-channel seed-id (cleared, then sparsely written by pass_seeds).
  const seedTex = createStorageTexture2D(device, dim, formats.jfaStorage, 'roads.seed');
  // JFA ping-pong state (rgba32uint: id, sx, sy, _).
  const jfaA = createStorageTexture2D(device, dim, 'rgba32uint', 'roads.jfaA');
  const jfaB = createStorageTexture2D(device, dim, 'rgba32uint', 'roads.jfaB');
  // Mask ping-pong: edges write A, minor reads A & writes B.
  const maskA = createStorageTexture2D(device, dim, formats.roadStorage, 'roads.maskA');
  const maskB = createStorageTexture2D(device, dim, formats.roadStorage, 'roads.maskB');

  const seedView = seedTex.createView({ label: 'roads.seed.view' });
  const jfaAView = jfaA.createView({ label: 'roads.jfaA.view' });
  const jfaBView = jfaB.createView({ label: 'roads.jfaB.view' });
  const maskAView = maskA.createView({ label: 'roads.maskA.view' });
  const maskBView = maskB.createView({ label: 'roads.maskB.view' });

  // --- Uniforms. seed + jfaStep + roadRadius + gridSpacing + lineWidth + 3 pad → 32 bytes. ---
  const paramsBuffer = createUniformBuffer(device, 32, 'roads.params');
  {
    const buf = new ArrayBuffer(32);
    const u32 = new Uint32Array(buf);
    const f32 = new Float32Array(buf);
    u32[0] = opts.seed >>> 0;
    u32[1] = 0;                       // jfaStep — overwritten per JFA pass
    f32[2] = config.roadRadius;
    f32[3] = config.roadGridSpacing;
    f32[4] = config.roadLineWidth;
    u32[5] = 0;
    u32[6] = 0;
    u32[7] = 0;
    device.queue.writeBuffer(paramsBuffer, 0, buf);
  }

  // --- Pipelines (one per entry point; auto-layout per pipeline). ---
  const pClear = createComputePipeline(device, wgsl, 'pass_clear_seed', 'auto', 'roads.clear');
  const pSeeds = createComputePipeline(device, wgsl, 'pass_seeds', 'auto', 'roads.seeds');
  const pInit = createComputePipeline(device, wgsl, 'pass_jfa_init', 'auto', 'roads.jfaInit');
  const pJfa = createComputePipeline(device, wgsl, 'pass_jfa', 'auto', 'roads.jfa');
  const pEdges = createComputePipeline(device, wgsl, 'pass_edges', 'auto', 'roads.edges');
  const pMinor = createComputePipeline(device, wgsl, 'pass_minor', 'auto', 'roads.minor');

  // --- Bind groups. Each pipeline's auto layout reflects only the bindings its
  // entry point statically references. Passes that don't read `params` (binding 0)
  // — clear, jfaInit, edges — must NOT include binding 0 in their bind group.

  const bgClear = device.createBindGroup({
    label: 'roads.clear.bg',
    layout: pClear.getBindGroupLayout(0),
    entries: [
      { binding: 1, resource: seedView },
    ],
  });

  const bgSeeds = device.createBindGroup({
    label: 'roads.seeds.bg',
    layout: pSeeds.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: paramsBuffer } },
      { binding: 2, resource: height.view },
      { binding: 3, resource: biome.view },
      { binding: 4, resource: seedView },
    ],
  });

  const bgInit = device.createBindGroup({
    label: 'roads.jfaInit.bg',
    layout: pInit.getBindGroupLayout(0),
    entries: [
      { binding: 5, resource: seedView },
      { binding: 6, resource: jfaAView },
    ],
  });

  // JFA ping-pong: alternate read/write between jfaA and jfaB.
  const bgJfaAtoB = device.createBindGroup({
    label: 'roads.jfa.AtoB.bg',
    layout: pJfa.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: paramsBuffer } },
      { binding: 7, resource: jfaAView },
      { binding: 8, resource: jfaBView },
    ],
  });
  const bgJfaBtoA = device.createBindGroup({
    label: 'roads.jfa.BtoA.bg',
    layout: pJfa.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: paramsBuffer } },
      { binding: 7, resource: jfaBView },
      { binding: 8, resource: jfaAView },
    ],
  });

  // ---------- Run all passes. ----------

  // Step 1: clear seed texture.
  {
    const enc = device.createCommandEncoder({ label: 'roads.clear.enc' });
    const p = enc.beginComputePass({ label: 'roads.clear.pass' });
    p.setPipeline(pClear);
    p.setBindGroup(0, bgClear);
    p.dispatchWorkgroups(dispatch, dispatch, 1);
    p.end();
    device.queue.submit([enc.finish()]);
  }

  // Step 2: seed pass — 1 workgroup of 8 threads, 1 thread per stratum.
  {
    const enc = device.createCommandEncoder({ label: 'roads.seeds.enc' });
    const p = enc.beginComputePass({ label: 'roads.seeds.pass' });
    p.setPipeline(pSeeds);
    p.setBindGroup(0, bgSeeds);
    p.dispatchWorkgroups(1, 1, 1);
    p.end();
    device.queue.submit([enc.finish()]);
  }

  // Step 3: JFA init — broadcast seed positions into jfaA.
  {
    const enc = device.createCommandEncoder({ label: 'roads.jfaInit.enc' });
    const p = enc.beginComputePass({ label: 'roads.jfaInit.pass' });
    p.setPipeline(pInit);
    p.setBindGroup(0, bgInit);
    p.dispatchWorkgroups(dispatch, dispatch, 1);
    p.end();
    device.queue.submit([enc.finish()]);
  }

  // Step 4: 10 JFA passes, halving step.
  // After init, "current" state lives in jfaA. Each pass reads from current and
  // writes to alt; we then flip. After an even number of passes (10), the
  // final state is back in jfaA.
  let readsFromA = true;
  for (let i = 0; i < config.jfaPasses; i++) {
    const step = 1 << (config.jfaPasses - 1 - i); // 512, 256, ..., 1
    device.queue.writeBuffer(
      paramsBuffer,
      4, // jfaStep is the second u32
      new Uint32Array([step >>> 0]).buffer,
    );

    const enc = device.createCommandEncoder({ label: `roads.jfa.${i}.enc` });
    const p = enc.beginComputePass({ label: `roads.jfa.${i}.pass` });
    p.setPipeline(pJfa);
    p.setBindGroup(0, readsFromA ? bgJfaAtoB : bgJfaBtoA);
    p.dispatchWorkgroups(dispatch, dispatch, 1);
    p.end();
    device.queue.submit([enc.finish()]);

    readsFromA = !readsFromA;
  }

  // Final JFA state is in jfaA if `readsFromA` flipped back to true (i.e. after even passes).
  const finalJfaView = readsFromA ? jfaAView : jfaBView;

  // Step 5: edges — read final JFA + roadRadius from params, write majors into maskA.
  const bgEdges = device.createBindGroup({
    label: 'roads.edges.bg',
    layout: pEdges.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: paramsBuffer } },
      { binding: 9, resource: finalJfaView },
      { binding: 10, resource: biome.view },
      { binding: 11, resource: maskAView },
    ],
  });
  {
    const enc = device.createCommandEncoder({ label: 'roads.edges.enc' });
    const p = enc.beginComputePass({ label: 'roads.edges.pass' });
    p.setPipeline(pEdges);
    p.setBindGroup(0, bgEdges);
    p.dispatchWorkgroups(dispatch, dispatch, 1);
    p.end();
    device.queue.submit([enc.finish()]);
  }

  // Step 6: minor — OR the perturbed grid on top of maskA into maskB.
  const bgMinor = device.createBindGroup({
    label: 'roads.minor.bg',
    layout: pMinor.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: paramsBuffer } },
      { binding: 12, resource: finalJfaView },
      { binding: 13, resource: biome.view },
      { binding: 14, resource: maskAView },
      { binding: 15, resource: maskBView },
    ],
  });
  {
    const enc = device.createCommandEncoder({ label: 'roads.minor.enc' });
    const p = enc.beginComputePass({ label: 'roads.minor.pass' });
    p.setPipeline(pMinor);
    p.setBindGroup(0, bgMinor);
    p.dispatchWorkgroups(dispatch, dispatch, 1);
    p.end();
    device.queue.submit([enc.finish()]);
  }

  // ---------- Cleanup intermediates; keep the final JFA + maskB as outputs. ----------
  const finalJfaTexture = readsFromA ? jfaA : jfaB;
  const loserJfaTexture = readsFromA ? jfaB : jfaA;

  seedTex.destroy();
  loserJfaTexture.destroy();
  maskA.destroy();
  paramsBuffer.destroy();

  return {
    mask: maskB,
    maskView: maskBView,
    jfa: finalJfaTexture,
    jfaView: finalJfaView,
    destroy() {
      maskB.destroy();
      finalJfaTexture.destroy();
    },
  };
}
